// src/features/auth/components/BackupCodesModal.tsx
import React, { useState } from 'react';
import { 
  ShieldExclamationIcon, 
  DocumentArrowDownIcon, 
  ClipboardDocumentIcon,
  CheckIcon,
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';

interface BackupCodesModalProps {
  backupCodes: string[];
  onAcknowledged: () => void;
}

const BackupCodesModal: React.FC<BackupCodesModalProps> = ({ backupCodes, onAcknowledged }) => {
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const handleCopyCodes = async () => {
    try {
      const codesText = backupCodes.join('\n');
      await navigator.clipboard.writeText(codesText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      
    }
  };

  const handleDownloadCodes = () => {
    const codesText = [
      'Two-Factor Authentication Backup Codes',
      '==========================================',
      '',
      'IMPORTANT: Keep these codes safe and secure!',
      'Each code can only be used once.',
      '',
      'Generated on: ' + new Date().toLocaleDateString(),
      '',
      'Backup Codes:',
      ...backupCodes.map((code, index) => `${index + 1}. ${code}`)
    ].join('\n');

    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `2fa-backup-codes-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleContinue = () => {
    if (acknowledged) {
      onAcknowledged();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <ShieldExclamationIcon className="h-12 w-12 text-orange-500 mx-auto mb-3" />
        <h4 className="text-lg font-medium text-theme-text dark:text-dark-theme-text mb-2">
          Save Your Backup Codes
        </h4>
        <p className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">
          These backup codes will allow you to access your account if you lose your authenticator device
        </p>
      </div>

      {/* Important Warning */}
      <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md p-4">
        <div className="flex items-start space-x-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
          <div>
            <h5 className="text-sm font-medium text-orange-800 dark:text-orange-200 mb-1">
              Important Security Information
            </h5>
            <ul className="text-xs text-orange-700 dark:text-orange-300 space-y-1">
              <li>• Each backup code can only be used once</li>
              <li>• Store these codes in a safe, secure location</li>
              <li>• Don't share these codes with anyone</li>
              <li>• You can generate new codes anytime in your security settings</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Backup Codes Display */}
      <div className="bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary rounded-md p-4">
        <div className="flex items-center justify-between mb-3">
          <h5 className="text-sm font-medium text-theme-text dark:text-dark-theme-text">
            Your Backup Codes
          </h5>
          <div className="flex space-x-2">
            <button
              onClick={handleCopyCodes}
              className="inline-flex items-center px-2 py-1 text-xs text-primary-light dark:text-primary-dark hover:bg-theme-bg dark:hover:bg-dark-theme-bg rounded transition-colors duration-150"
              title="Copy all codes"
            >
              {copied ? (
                <>
                  <CheckIcon className="h-3 w-3 mr-1" />
                  Copied
                </>
              ) : (
                <>
                  <ClipboardDocumentIcon className="h-3 w-3 mr-1" />
                  Copy
                </>
              )}
            </button>
            <button
              onClick={handleDownloadCodes}
              className="inline-flex items-center px-2 py-1 text-xs text-primary-light dark:text-primary-dark hover:bg-theme-bg dark:hover:bg-dark-theme-bg rounded transition-colors duration-150"
              title="Download as text file"
            >
              <DocumentArrowDownIcon className="h-3 w-3 mr-1" />
              Download
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          {backupCodes.map((code, index) => (
            <div
              key={index}
              className="bg-theme-bg dark:bg-dark-theme-bg border border-theme-border dark:border-dark-theme-border rounded px-3 py-2"
            >
              <code className="text-sm font-mono text-theme-text dark:text-dark-theme-text">
                {code}
              </code>
            </div>
          ))}
        </div>
      </div>

      {/* Storage Recommendations */}
      <div className="bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary rounded-md p-4">
        <h5 className="text-sm font-medium text-theme-text dark:text-dark-theme-text mb-2">
          Recommended Storage Options
        </h5>
        <ul className="text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary space-y-1">
          <li>• Password manager (1Password, Bitwarden, etc.)</li>
          <li>• Secure note-taking app with encryption</li>
          <li>• Physical printout in a safe location</li>
          <li>• Encrypted file on a secure device</li>
        </ul>
      </div>

      {/* Acknowledgment */}
      <div className="space-y-4">
        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-1 h-4 w-4 text-primary-light dark:text-primary-dark focus:ring-primary-light dark:focus:ring-primary-dark border-theme-border dark:border-dark-theme-border rounded"
          />
          <span className="text-sm text-theme-text dark:text-dark-theme-text">
            I have safely stored these backup codes and understand that each code can only be used once.
          </span>
        </label>

        <div className="flex justify-center">
          <button
            onClick={handleContinue}
            disabled={!acknowledged}
            className="px-6 py-2 bg-gradient-button text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light dark:focus:ring-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-150"
          >
            Continue to Complete Setup
          </button>
        </div>
      </div>
    </div>
  );
};

export default BackupCodesModal;
